import { RouterLink as Link } from "../../router";
import { ExternalLink } from "../../icons";
import { useApi } from "../../api/client";
import { help } from "../../api/contracts/help";
import { Card, CardContent } from "../../card";

export interface HelpExternalLinksFooterProps {
  /** Optional title (default: "Need more help?"). */
  title?: string;
  className?: string;
}

/**
 * Renders the configured external links as a pill row. Reads from
 * `useApi(help.links.list, { enabled_only: true })` so disabled or
 * empty-URL rows drop out. Used on landing, category, and article
 * pages — the article footer surface is the same as the user
 * dropdown's link list.
 *
 * Internal links (e.g. /help/changelog) stay internal (no
 * target="_blank"); everything else opens in a new tab.
 */
export function HelpExternalLinksFooter({
  title = "Need more help?",
  className,
}: HelpExternalLinksFooterProps) {
  const { data } = useApi(help.links.list, {
    query: { enabled_only: true },
  });
  const links = data?.links ?? [];
  if (links.length === 0) return null;
  return (
    <Card className={className}>
      <CardContent className="space-y-3">
        <p className="type-body text-foreground">{title}</p>
        <div className="flex flex-wrap gap-2">
          {links.map((l) => {
            const isInternal = l.url.startsWith("/");
            return (
              <Link
                key={l.id}
                href={l.url || "#"}
                target={isInternal ? undefined : "_blank"}
                rel={isInternal ? undefined : "noopener noreferrer"}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary/40 px-2.5 py-1 type-body text-foreground hover:bg-secondary/70"
              >
                <span>{l.label}</span>
                {!isInternal ? (
                  <ExternalLink aria-hidden className="size-3 text-muted-foreground" />
                ) : null}
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
